import React from 'react';
import { COLORS, Loader, Tooltip, typographyStyles } from '@livecommerce/design-system';
import { useVodStatsQuery } from './hooks/useVodStatsQuery.js';
import { formatTime } from 'helpers/formatTime.js';
import { abbreviateNumber, formatAmount } from 'utils/index.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import usePermissions from 'hooks/usePermissions.jsx';
import { PERMISSIONS } from 'shared-permissions';
import HeaderItem from 'components-v3/ProductHeader/HeaderItem.jsx';
import { styled } from '@linaria/react';
import useOrgType from 'hooks/useOrgType.js';
const { getBodyText } = typographyStyles;

const { VOD_STATS } = PERMISSIONS;

const Value = styled.div`
  color: ${COLORS.white};
  ${getBodyText(18, 500)};
  line-height: 27px;
`;

const StyledTooltip = styled(Tooltip)`
  height: 100%;
  & > div:not(.tooltip) { // Workaround to prevent Tooltip from taking 100% height
    height: 100%;
  }
`;

const TimeframeTooltip = ({ children }) => {
  const { t } = useLocalization();

  return (
    <StyledTooltip
      tooltipText={t('vodGlobalStats.timeframeTooltipText', { days: 30 })}
      variant="grey"
      tooltipPosition="left"
    >
      {children}
    </StyledTooltip>
  );
};

const VodStats = () => {
  const { t } = useLocalization();
  const timeframeDays = 30;
  const { hasPermission } = usePermissions();
  const hasReadVodStatsPermission = hasPermission(VOD_STATS, 'read');
  const orgType = useOrgType();
  const { data = {}, isLoading, error } = useVodStatsQuery({
    timeframeDays,
  }, {
    enabled: hasReadVodStatsPermission,
  });

  if (!hasReadVodStatsPermission) return null;
  if (error) return null;
  if (isLoading) {
    return <Loader />;
  }

  const totalIndirectSalesAmount = (data.totalIndirectSalesAmount > 0 && data.displayCurrency) ?
    formatAmount({ amount: data.totalIndirectSalesAmount, currency: data.displayCurrency, precision: 0 }) :
    '-';

  const uniqueViewersCount = data.uniqueViewersCount > 0 ?
    abbreviateNumber(data.uniqueViewersCount) :
    '-';

  const totalViewingSeconds = data.totalViewingSeconds > 0 ?
    formatTime(data.totalViewingSeconds) :
    '-';

  const totalViewsCount = data.totalViewsCount > 0 ?
    abbreviateNumber(data.totalViewsCount) :
    '-';

  return (
    <>
      <TimeframeTooltip>
        <HeaderItem label={t('vodGlobalStats.influencedSales')}>
          <Value>{totalIndirectSalesAmount}</Value>
        </HeaderItem>
      </TimeframeTooltip>
      <TimeframeTooltip>
        <HeaderItem label={t('vodGlobalStats.uniqueViewers')}>
          <Value>{uniqueViewersCount}</Value>
        </HeaderItem>
      </TimeframeTooltip>
      <TimeframeTooltip>
        <HeaderItem label={t('vodGlobalStats.viewingHours')}>
          <Value>{totalViewingSeconds}</Value>
        </HeaderItem>
      </TimeframeTooltip>
      {orgType === 'trial' && (
        <TimeframeTooltip>
          <HeaderItem label={t('vodGlobalStats.totalViewsCount')}>
            <Value>{totalViewsCount}</Value>
          </HeaderItem>
        </TimeframeTooltip>
      )}
    </>
  );
};

VodStats.propTypes = {};

export default VodStats;
